import { useState, useEffect } from 'react';
import * as Store from '@apply/store';
import { ShowIf, SubSectionHeader, DataRow, Accordion, validEntityType } from '@apply/ui';
import { BuildRentTypeOptions, LoanType, LoanTypeOptions } from '@apply/constants';

const getLoanType = (borrower: any) => {
    if (borrower ?? borrower.loanType) {
        if (borrower.loanType === LoanType.BuildRent) {
            return BuildRentTypeOptions.find((t) => t.id === borrower?.buildRentTypeId)?.name;
        } else {
            return LoanTypeOptions.find((t) => t.id === borrower?.loanType)?.name;
        }
    } else {
        return 'Unknown';
    }
};

export interface BorrowerReviewProps {
    borrower: Store.BorrowerStore.Types.BorrowerState;
    apiId?: string;
    printMode?: boolean;
    formCompleted: boolean;
    token: string;
    collapsable: boolean;
}

export const BorrowerReview: React.FC<BorrowerReviewProps> = ({
    borrower,
    apiId,
    printMode = false,
    formCompleted,
    token,
    collapsable,
}) => {
    return (
        <>
            <div className='scrollable-content'>
                <div
                    className='accordion'
                    data-accordion=''
                    data-allow-all-closed='true'
                    data-multi-expand='true'
                >
                    <ShowIf showIf={printMode}>
                        <h1 className='traditional-header'>{getLoanType(borrower)} Loan</h1>
                    </ShowIf>
                    <ShowIf showIf={!printMode}>
                        <h1 className='traditional-header'>Review and Submit</h1>
                    </ShowIf>

                    <Accordion
                        title={
                            <SubSectionHeader
                                title='Borrower'
                                linkDestination={`/borrower/${apiId ? apiId : ''}${token ? '?token=' + token : ''}`}
                                printMode={printMode}
                                formCompleted={formCompleted}
                            />
                        }
                        isCollapsable={collapsable}
                    >
                        <DataRow value={getLoanType(borrower)}> Loan Type</DataRow>
                        <div className='row'>
                            <div
                                style={{
                                    paddingRight: '50px',
                                    paddingLeft: '50px',
                                }}
                            >
                                <strong>
                                    I acknowledge that this application is for a BUSINESS PURPOSE AND that the property is or will
                                    be OWNED BY A CORPORATE ENTITY.
                                </strong>
                            </div>
                        </div>

                        <ShowIf showIf={borrower.isFirstLoan}>
                            <DataRow value={!!borrower?.firstLoanApplicationWithBfi}>
                                Is this your first loan application with BFI?
                            </DataRow>
                            <DataRow value={borrower?.howDidYouHear}>How did you find us?</DataRow>
                        </ShowIf>

                        <ShowIf showIf={borrower.shouldAskLegalQuestions}>
                            <div className='row columns header'>
                                <p>
                                    <strong>
                                        Currently, are you, your organization, and/or an owner or officer of your organization:
                                    </strong>
                                </p>
                            </div>

                            <DataRow value={borrower?.currentlyQuestions?.defendantInLegalActions}>
                                A defendant in any suits or legal actions?
                            </DataRow>
                            <DataRow value={borrower?.currentlyQuestions?.involvedInJudgements}>
                                Involved in any judgements, liens or surety actions?
                            </DataRow>
                            <DataRow value={borrower?.currentlyQuestions?.delinquentOnDebts}>
                                Delinquent on any real estate debts?
                            </DataRow>
                            <DataRow value={borrower?.currentlyQuestions?.delinquentOnTaxes}>Delinquent on any taxes?</DataRow>
                            <ShowIf showIf={borrower?.currentlyQuestions?.financiallyWorseOff !== undefined}>
                                <DataRow value={borrower?.currentlyQuestions?.financiallyWorseOff}>
                                    Financially worse off, to a material degree, relative to the time of your last loan
                                    application with BFI?
                                </DataRow>
                            </ShowIf>
                            <ShowIf showIf={!!borrower?.currentlyQuestions?.explanations}>
                                <DataRow value={borrower?.currentlyQuestions?.explanations}>
                                    Briefly explain any items you answered yes:
                                </DataRow>
                            </ShowIf>
                        </ShowIf>

                        <ShowIf showIf={borrower?.loanType === LoanType.Rental || borrower?.loanType === LoanType.BuildRent}>
                            <DataRow value={borrower?.usingPropertyManagementCompany}>
                                Will you use a property management company?
                            </DataRow>

                            <ShowIf showIf={borrower?.usingPropertyManagementCompany === true}>
                                <DataRow value={borrower?.propertyManagementCompany}>Property Contact</DataRow>
                            </ShowIf>

                            <ShowIf
                                showIf={
                                    (borrower?.loanType === LoanType.Rental || borrower?.loanType === LoanType.BuildRent) &&
                                    !!borrower?.usingPropertyManagementCompany
                                }
                            >
                                <DataRow value={borrower?.propertyManagementCompany?.managementAgreement}>
                                    Management Agreement
                                </DataRow>

                                <ShowIf
                                    showIf={
                                        borrower?.propertyManagementCompany?.managementAgreement?.handleMode === 'providelater'
                                    }
                                >
                                    <div className='row'>
                                        <div
                                            style={{
                                                paddingRight: '50px',
                                                paddingLeft: '50px',
                                            }}
                                        >
                                            <strong>
                                                <em>NOTE:</em>
                                            </strong>
                                            &nbsp; Management Agreement is needed to process your application and order a
                                            valuation. Please submit ASAP via&nbsp;
                                            <a href='mailto:team@builderfinance.com'>team@builderfinance.com</a>
                                            &nbsp; or&nbsp;
                                            <a
                                                href='https://fileshare.bfiweb.com'
                                                target='_blank'
                                            >
                                                https://fileshare.bfiweb.com
                                            </a>
                                        </div>
                                    </div>
                                </ShowIf>
                            </ShowIf>
                        </ShowIf>

                        <DataRow value={borrower?.principal}>My Contact Information</DataRow>

                        <DataRow value={borrower?.entity.name}>Borrower Entity</DataRow>

                        <ShowIf showIf={!validEntityType(borrower?.entity.name)}>
                            <DataRow
                                value={
                                    Store.BorrowerStore.Types.entityTypes.find((et) => et.id === borrower?.entity?.typeId)?.name
                                }
                            >
                                Confirm Entity Type
                            </DataRow>
                        </ShowIf>

                        <DataRow value={borrower?.entity?.address}>Business Mailing Address</DataRow>

                        <ShowIf showIf={borrower?.finalizeThirdPartyClaim && borrower?.finalizeThirdPartyClaim !== 'yes'}>
                            <DataRow value={borrower?.finalizeThirdPartyClaim}>Broker</DataRow>
                        </ShowIf>
                        <ShowIf showIf={borrower?.finalizeThirdPartyClaim && borrower?.finalizeThirdPartyClaim === 'yes'}>
                            <DataRow value={borrower?.finalizeThirdPartyName}>Broker</DataRow>
                        </ShowIf>
                    </Accordion>
                </div>
            </div>
        </>
    );
};
